import React, { useState, useEffect } from 'react';
import Config from '../config.json';

/**
 * CustomerVerificationForm
 * 
 * This component displays a form for customer verification based on
 * the Customer Verification Form template.
 * 
 * Props:
 * - showModal: boolean to control visibility
 * - onClose: function to call when modal is closed
 * - accountData: the account object with basic information (used as fallback)
 * - accountDetails: detailed account information fetched from API (preferred data source)
 * - onSubmitSuccess: callback function after successful submission
 */
const CustomerVerificationForm = ({ 
    showModal, 
    onClose, 
    accountData,
    accountDetails,
    onSubmitSuccess
}) => {
    // Initialize form state using accountDetails first, then accountData as fallback
    const [formData, setFormData] = useState({
        // Account Information
        date: new Date().toISOString().split('T')[0],
        dealerSeller: accountDetails?.dealerSeller || '',
        buyerName: `${accountDetails?.firstName || accountData?.firstName || ''} ${accountDetails?.lastName || accountData?.lastName || ''}`.trim(),
        coBuyerName: accountDetails?.coBuyerName || '',
        
        // Vehicle Verification
        year: accountDetails?.year || accountData?.year || '',
        make: accountDetails?.make || accountData?.make || '',
        model: accountDetails?.model || accountData?.model || '',
        mileage: accountDetails?.mileage || '',
        insuranceCarrier: accountDetails?.insuranceCarrier || '',
        policyNo: accountDetails?.policyNo || '',
        gpsInstalled: accountDetails?.gpsInstalled || 'unknown',
        vehicleDriver: accountDetails?.vehicleDriver || '',
        repairsNeeded: accountDetails?.repairsNeeded || '',
        
        // Contract Verification
        paymentAmount: accountDetails?.paymentAmount || accountData?.paymentAmount || '',
        frequency: accountDetails?.frequency || accountData?.frequency || '',
        lastPaymentDate: accountDetails?.lastPaymentDate || '',
        
        // Primary Buyer Verification
        primaryStreetAddress: accountDetails?.primaryStreetAddress || '',
        primaryCity: accountDetails?.primaryCity || '',
        primaryState: accountDetails?.primaryState || '',
        primaryZip: accountDetails?.primaryZip || '',
        primaryBestContactPhone: accountDetails?.primaryBestContactPhone || accountData?.primaryBestContactPhone || '',
        primaryBestContactPhoneType: 'cell',
        primaryAlternatePhone: accountDetails?.primaryAlternatePhone || accountData?.primaryAlternatePhone || '',
        primaryAlternatePhoneType: accountDetails?.primaryAlternatePhoneType || accountData?.primaryAlternatePhoneType || 'cell',
        primaryEmployer: accountDetails?.primaryEmployer || '',
        primaryEmployerPhone: accountDetails?.primaryEmployerPhone || '',
        primaryPosition: accountDetails?.primaryPosition || '',
        primaryShift: accountDetails?.primaryShift || '',
        
        // Co-Buyer Verification
        hasCoBuyer: !!accountDetails?.coBuyerName,
        coBuyerName: accountDetails?.coBuyerName || '',
        coBuyerStreetAddress: accountDetails?.coBuyerStreetAddress || '',
        coBuyerCity: accountDetails?.coBuyerCity || '',
        coBuyerState: accountDetails?.coBuyerState || '',
        coBuyerZip: accountDetails?.coBuyerZip || '',
        coBuyerBestContactPhone: accountDetails?.coBuyerBestContactPhone || '',
        coBuyerBestContactPhoneType: accountDetails?.coBuyerBestContactPhoneType || 'cell',
        coBuyerAlternatePhone: accountDetails?.coBuyerAlternatePhone || '',
        coBuyerAlternatePhoneType: accountDetails?.coBuyerAlternatePhoneType || 'cell',
        coBuyerEmployer: accountDetails?.coBuyerEmployer || '',
        coBuyerEmployerPhone: accountDetails?.coBuyerEmployerPhone || '',
        coBuyerPosition: accountDetails?.coBuyerPosition || '',
        coBuyerShift: accountDetails?.coBuyerShift || '',
        
        // Miscellaneous Verification
        authorizedThirdParty: accountDetails?.authorizedThirdParty || '',
        thirdPartyRelationship: accountDetails?.thirdPartyRelationship || '',
        hasEmailForCorrespondence: accountDetails?.hasEmailForCorrespondence ?? (accountDetails?.emailForCorrespondence ? 'yes' : 'no'),
        emailForCorrespondence: accountDetails?.emailForCorrespondence || accountData?.emailForCorrespondence || '',
        
        // Consent to Contact Verification
        consentToContact: accountDetails?.consentToContact || 'no',
        verifiedCellPhone: accountDetails?.verifiedCellPhone || accountData?.verifiedCellPhone || '',
        
        // References (array of reference objects)
        references: accountDetails?.references || [
            { name: '', relationship: '', phoneNo: '' },
            { name: '', relationship: '', phoneNo: '' },
            { name: '', relationship: '', phoneNo: '' }
        ],
        
        // Additional Comments
        additionalComments: accountDetails?.additionalComments || ''
    });
    
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [warnings, setWarnings] = useState({});
    const timeoutRef = React.useRef(null);
    
    // Update form data when accountDetails changes
    useEffect(() => {
        if (showModal && accountDetails) {
            // Reset the success state when the form is opened
            setSubmitSuccess(false);
            setFormData({
                // Account Information
                date: new Date().toISOString().split('T')[0],
                dealerSeller: accountDetails?.dealerSeller || '',
                buyerName: `${accountDetails?.firstName || accountData?.firstName || ''} ${accountDetails?.lastName || accountData?.lastName || ''}`.trim(),
                coBuyerName: accountDetails?.coBuyerName || '',
                
                // Vehicle Verification
                year: accountDetails?.year || accountData?.year || '',
                make: accountDetails?.make || accountData?.make || '',
                model: accountDetails?.model || accountData?.model || '',
                mileage: accountDetails?.mileage || '',
                insuranceCarrier: accountDetails?.insuranceCarrier || '',
                policyNo: accountDetails?.policyNo || '',
                gpsInstalled: accountDetails?.gpsInstalled || 'unknown',
                vehicleDriver: accountDetails?.vehicleDriver || '',
                repairsNeeded: accountDetails?.repairsNeeded || '',
                
                // Contract Verification
                paymentAmount: accountDetails?.paymentAmount || accountData?.paymentAmount || '',
                frequency: accountDetails?.frequency || accountData?.frequency || '',
                lastPaymentDate: accountDetails?.lastPaymentDate || '',
                
                // Primary Buyer Verification
                primaryStreetAddress: accountDetails?.primaryStreetAddress || '',
                primaryCity: accountDetails?.primaryCity || '',
                primaryState: accountDetails?.primaryState || '',
                primaryZip: accountDetails?.primaryZip || '',
                primaryBestContactPhone: accountDetails?.primaryBestContactPhone || accountData?.primaryBestContactPhone || '',
                primaryBestContactPhoneType: accountDetails?.primaryBestContactPhoneType || 'cell',
                primaryAlternatePhone: accountDetails?.primaryAlternatePhone || accountData?.primaryAlternatePhone || '',
                primaryAlternatePhoneType: accountDetails?.primaryAlternatePhoneType || accountData?.primaryAlternatePhoneType || 'cell',
                primaryEmployer: accountDetails?.primaryEmployer || '',
                primaryEmployerPhone: accountDetails?.primaryEmployerPhone || '',
                primaryPosition: accountDetails?.primaryPosition || '',
                primaryShift: accountDetails?.primaryShift || '',
                
                // Co-Buyer Verification
                hasCoBuyer: !!accountDetails?.coBuyerName,
                coBuyerName: accountDetails?.coBuyerName || '',
                coBuyerStreetAddress: accountDetails?.coBuyerStreetAddress || '',
                coBuyerCity: accountDetails?.coBuyerCity || '',
                coBuyerState: accountDetails?.coBuyerState || '',
                coBuyerZip: accountDetails?.coBuyerZip || '',
                coBuyerBestContactPhone: accountDetails?.coBuyerBestContactPhone || '',
                coBuyerBestContactPhoneType: accountDetails?.coBuyerBestContactPhoneType || 'cell',
                coBuyerAlternatePhone: accountDetails?.coBuyerAlternatePhone || '',
                coBuyerAlternatePhoneType: accountDetails?.coBuyerAlternatePhoneType || 'cell',
                coBuyerEmployer: accountDetails?.coBuyerEmployer || '',
                coBuyerEmployerPhone: accountDetails?.coBuyerEmployerPhone || '',
                coBuyerPosition: accountDetails?.coBuyerPosition || '',
                coBuyerShift: accountDetails?.coBuyerShift || '',
                
                // Miscellaneous Verification
                authorizedThirdParty: accountDetails?.authorizedThirdParty || '',
                thirdPartyRelationship: accountDetails?.thirdPartyRelationship || '',
                hasEmailForCorrespondence: accountDetails?.hasEmailForCorrespondence ?? (accountDetails?.emailForCorrespondence ? 'yes' : 'no'),
                emailForCorrespondence: accountDetails?.emailForCorrespondence || accountData?.emailForCorrespondence || '',
                
                // Consent to Contact Verification
                consentToContact: accountDetails?.consentToContact || 'no',
                verifiedCellPhone: accountDetails?.verifiedCellPhone || accountData?.verifiedCellPhone || '',
                
                // References (array of reference objects)
                references: accountDetails?.references || [
                    { name: '', relationship: '', phoneNo: '' },
                    { name: '', relationship: '', phoneNo: '' },
                    { name: '', relationship: '', phoneNo: '' }
                ],
                
                // Additional Comments
                additionalComments: accountDetails?.additionalComments || ''
            });
        }
    }, [accountDetails, accountData, showModal]);
    
    // When modal opens/closes, control body overflow
    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
            
            // Add event listener for Escape key
            const handleEscape = (event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            };
            
            document.addEventListener('keydown', handleEscape);
            
            return () => {
                document.removeEventListener('keydown', handleEscape);
                document.body.style.overflow = 'auto';
                
                // Clear the timeout when component unmounts or modal closes
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            };
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [showModal, onClose]);
    
    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Clear validation error when field is updated
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    
    // Handle references change
    const handleReferenceChange = (index, field, value) => {
        setFormData(prev => {
            const updatedReferences = [...prev.references];
            updatedReferences[index] = { 
                ...updatedReferences[index], 
                [field]: value 
            };
            return { ...prev, references: updatedReferences };
        });
        
        // Clear validation error for this reference field
        const errorKey = `references[${index}].${field}`;
        if (validationErrors[errorKey]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };
    
    // Validation function
    const validateForm = () => {
        const errors = {};
        const warnings = {};
        
        // Required fields validation
        const requiredFields = [
            { key: 'buyerName', label: 'Buyer Name' },
            { key: 'date', label: 'Date' },
            { key: 'dealerSeller', label: 'Dealer/Seller' },
            { key: 'year', label: 'Vehicle Year' },
            { key: 'make', label: 'Vehicle Make' },
            { key: 'model', label: 'Vehicle Model' },
            { key: 'paymentAmount', label: 'Payment Amount' },
            { key: 'primaryStreetAddress', label: 'Primary Street Address' },
            { key: 'primaryCity', label: 'Primary City' },
            { key: 'primaryState', label: 'Primary State' },
            { key: 'primaryZip', label: 'Primary ZIP' },
            { key: 'primaryBestContactPhone', label: 'Primary Contact Phone' },
            { key: 'consentToContact', label: 'Consent to Contact' }
        ];
        
        requiredFields.forEach(field => {
            if (!formData[field.key]) {
                errors[field.key] = `${field.label} is required`;
            }
        });
        
        // Validate co-buyer fields if hasCoBuyer is checked
        if (formData.hasCoBuyer) {
            const coBuyerRequiredFields = [
                { key: 'coBuyerName', label: 'Co-Buyer Name' },
                { key: 'coBuyerStreetAddress', label: 'Co-Buyer Street Address' },
                { key: 'coBuyerCity', label: 'Co-Buyer City' },
                { key: 'coBuyerState', label: 'Co-Buyer State' },
                { key: 'coBuyerZip', label: 'Co-Buyer ZIP' },
                { key: 'coBuyerBestContactPhone', label: 'Co-Buyer Contact Phone' }
            ];
            
            coBuyerRequiredFields.forEach(field => {
                if (!formData[field.key]) {
                    errors[field.key] = `${field.label} is required`;
                }
            });
        }
        
        // Soft validation for references - only add warnings, no hard errors
        let validReferenceCount = 0;
        formData.references.forEach((ref) => {
            if (ref.name && ref.relationship && ref.phoneNo) {
                validReferenceCount++;
            }
        });
        
        // Add a gentle suggestion about references
        if (validReferenceCount < 3) {
            warnings.references = 'We recommend providing at least 3 complete references';
        }
        
        // Validate email if hasEmailForCorrespondence is 'yes'
        if (formData.hasEmailForCorrespondence === 'yes' && !formData.emailForCorrespondence) {
            errors.emailForCorrespondence = 'Email address is required';
        }
        
        // Validate verified cell phone if consentToContact is 'yes'
        if (formData.consentToContact === 'yes' && !formData.verifiedCellPhone) {
            errors.verifiedCellPhone = 'Verified cell phone is required';
        }
        
        // Update the warnings state
        setWarnings(warnings);
        
        return errors;
    };
    
    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        
        setIsSubmitting(true);
        setSubmitError('');
        
        try {
            // Determine API URL based on hostname
            const apiURL = window.location.hostname === "192.168.1.45"
                ? "http://localhost:8383/loanverification/customerformsubmit"
                : "https://api.galeforceai.com/loanverification/customerformsubmit";
            
            // Prepare data to send to backend
            const submissionData = {
                accountId: accountData?._id || accountData?.id || accountData?.accountId,
                verificationData: formData
            };
            
            // API call to submit verification
            const response = await fetch(apiURL, {
                method: "POST",
                headers: {
                    "Authorization": Config.loanverificationrequest,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(submissionData)
            });
            
            if (!response.ok) {
                throw new Error(`Failed to submit verification (${response.status})`);
            }
            
            const result = await response.json();
            console.log('API call status:', response.ok ? 'Success' : 'Failed', response.status);
            
            setSubmitSuccess(true);
            setIsSubmitting(false);
            
            // Call the success callback if provided
            if (onSubmitSuccess && typeof onSubmitSuccess === 'function') {
                onSubmitSuccess(result);
            }
            
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            
            // Close the modal after successful submission (with slight delay for user to see confirmation)
            timeoutRef.current = setTimeout(() => {
                onClose();
                timeoutRef.current = null;
            }, 2000);
            
            // Show warnings as notifications if they exist
            if (Object.keys(warnings).length > 0) {
                // Display warnings to the user
                // This depends on your UI components, could be a toast, alert, or inline message
                showWarningNotification("Form saved, but has incomplete sections");
            }
            
        } catch (error) {
            setSubmitError(error.message || 'Failed to submit verification');
            setIsSubmitting(false);
        }
    };
    
    // Add a function to display warnings
    const showWarningNotification = (message) => {
        // Implement based on your UI framework
        // Example with a simple alert:
        alert(message + "\n\nPlease complete the references section when possible.");
        
        // Or with a toast notification component, etc.
    };
    
    if (!showModal) return null;
    
    // Define common input styles
    const inputStyle = {
        padding: '8px 12px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        fontSize: '14px',
        width: '100%',
        backgroundColor: '#fff',
        color: '#333'
    };
    
    const labelStyle = {
        display: 'block',
        marginBottom: '5px',
        color: '#e0e0e0',
        fontSize: '14px'
    };
    
    const fieldGroupStyle = {
        marginBottom: '15px'
    };
    
    const errorStyle = {
        color: '#ff6b6b',
        fontSize: '12px',
        marginTop: '4px'
    };
    
    const sectionStyle = {
        marginBottom: '25px',
        padding: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px'
    };
    
    const sectionTitleStyle = {
        borderBottom: '1px solid #444',
        paddingBottom: '10px',
        color: '#0099ff',
        marginTop: '0',
        marginBottom: '15px'
    };
    
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}
        onClick={onClose}
        >
            <div style={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                padding: '25px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '1200px',
                maxHeight: '90vh',
                overflowY: 'auto',
                color: '#e0e0e0',
                boxShadow: '0 5px 15px rgba(0,0,0,0.5)',
                border: '1px solid #333'
            }}
            onClick={(e) => e.stopPropagation()}
            >
                <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: '1px solid #444',
                    paddingBottom: '10px'
                }}>
                    <h2 style={{ color: '#fff', margin: 0, fontWeight: '500' }}>
                        Customer Verification Form: {formData.buyerName}
                    </h2>
                    <div>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                marginRight: '10px',
                                padding: '10px 20px',
                                backgroundColor: '#555',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            style={{
                                padding: '10px 30px',
                                backgroundColor: isSubmitting ? '#0066b2' : '#0099ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : 'Save'}
                        </button>
                    </div>
                </div>
                
                {submitSuccess ? (
                    <div style={{ 
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: 'rgba(39, 174, 96, 0.2)',
                        borderRadius: '6px',
                        border: '1px solid #27ae60'
                    }}>
                        <h3 style={{ color: '#2ecc71' }}>Verification Submitted Successfully!</h3>
                        <p>The verification form has been saved to the account.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {/* Account Information */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Account Information</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.date && <div style={errorStyle}>{validationErrors.date}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Dealer/Seller</label>
                                    <input
                                        type="text"
                                        name="dealerSeller"
                                        value={formData.dealerSeller}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.dealerSeller && <div style={errorStyle}>{validationErrors.dealerSeller}</div>}
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div style={fieldGroupStyle}>
                                        <label style={labelStyle}>Buyer Name</label>
                                        <input
                                            type="text"
                                            name="buyerName"
                                            value={formData.buyerName}
                                            onChange={handleChange}
                                            style={inputStyle}
                                        />
                                        {validationErrors.buyerName && <div style={errorStyle}>{validationErrors.buyerName}</div>}
                                    </div>
                                    
                                    <div style={fieldGroupStyle}>
                                        <label style={labelStyle}>Co-Buyer Name</label>
                                        <input
                                            type="text"
                                            name="coBuyerName"
                                            value={formData.coBuyerName}
                                            onChange={handleChange}
                                            style={inputStyle}
                                        />
                                        {validationErrors.coBuyerName && <div style={errorStyle}>{validationErrors.coBuyerName}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Vehicle Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Vehicle Verification</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Year</label>
                                    <input
                                        type="text"
                                        name="year"
                                        value={formData.year}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.year && <div style={errorStyle}>{validationErrors.year}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Make</label>
                                    <input
                                        type="text"
                                        name="make"
                                        value={formData.make}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.make && <div style={errorStyle}>{validationErrors.make}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Model</label>
                                    <input
                                        type="text"
                                        name="model"
                                        value={formData.model}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.model && <div style={errorStyle}>{validationErrors.model}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Mileage</label>
                                    <input
                                        type="text"
                                        name="mileage"
                                        value={formData.mileage}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.mileage && <div style={errorStyle}>{validationErrors.mileage}</div>}
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginTop: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Insurance Carrier</label>
                                    <input
                                        type="text"
                                        name="insuranceCarrier"
                                        value={formData.insuranceCarrier}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Policy No.</label>
                                    <input
                                        type="text"
                                        name="policyNo"
                                        value={formData.policyNo}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={labelStyle}>Is there a GPS device installed on your vehicle?</label>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="gpsInstalled"
                                            value="yes"
                                            checked={formData.gpsInstalled === 'yes'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Yes
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="gpsInstalled"
                                            value="no"
                                            checked={formData.gpsInstalled === 'no'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        No
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="gpsInstalled"
                                            value="unknown"
                                            checked={formData.gpsInstalled === 'unknown'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Unknown
                                    </label>
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={labelStyle}>Who drives the vehicle?</label>
                                <input
                                    type="text"
                                    name="vehicleDriver"
                                    value={formData.vehicleDriver}
                                    onChange={handleChange}
                                    style={inputStyle}
                                />
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={labelStyle}>Are any vehicle repairs needed?</label>
                                <textarea
                                    name="repairsNeeded"
                                    value={formData.repairsNeeded}
                                    onChange={handleChange}
                                    style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                                />
                            </div>
                        </div>
                        
                        {/* Contract Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Contract Verification</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Payment Amount</label>
                                    <input
                                        type="text"
                                        name="paymentAmount"
                                        value={formData.paymentAmount}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.paymentAmount && <div style={errorStyle}>{validationErrors.paymentAmount}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Frequency</label>
                                    <select
                                        name="frequency"
                                        value={formData.frequency}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    >
                                        <option value="">Select Frequency</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Bi-Weekly">Bi-Weekly</option>
                                        <option value="Semi-Monthly">Semi-Monthly</option>
                                        <option value="Monthly">Monthly</option>
                                    </select>
                                    {validationErrors.frequency && <div style={errorStyle}>{validationErrors.frequency}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Last Payment Date</label>
                                    <input
                                        type="date"
                                        name="lastPaymentDate"
                                        value={formData.lastPaymentDate}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Primary Buyer Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Primary Buyer Verification</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Street Address</label>
                                    <input
                                        type="text"
                                        name="primaryStreetAddress"
                                        value={formData.primaryStreetAddress}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.primaryStreetAddress && <div style={errorStyle}>{validationErrors.primaryStreetAddress}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>City</label>
                                    <input
                                        type="text"
                                        name="primaryCity"
                                        value={formData.primaryCity}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.primaryCity && <div style={errorStyle}>{validationErrors.primaryCity}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>State</label>
                                    <input
                                        type="text"
                                        name="primaryState"
                                        value={formData.primaryState}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.primaryState && <div style={errorStyle}>{validationErrors.primaryState}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>ZIP</label>
                                    <input
                                        type="text"
                                        name="primaryZip"
                                        value={formData.primaryZip}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                    {validationErrors.primaryZip && <div style={errorStyle}>{validationErrors.primaryZip}</div>}
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Best Contact Phone No.</label>
                                    <div style={{ display: 'flex' }}>
                                        <input
                                            type="text"
                                            name="primaryBestContactPhone"
                                            value={formData.primaryBestContactPhone}
                                            onChange={handleChange}
                                            style={{ ...inputStyle, borderTopRightRadius: 0, borderBottomRightRadius: 0, width: '70%' }}
                                        />
                                        <select
                                            name="primaryBestContactPhoneType"
                                            value={formData.primaryBestContactPhoneType}
                                            onChange={handleChange}
                                            style={{ ...inputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, width: '30%' }}
                                        >
                                            <option value="cell">Cell</option>
                                            <option value="home">Home</option>
                                        </select>
                                    </div>
                                    {validationErrors.primaryBestContactPhone && <div style={errorStyle}>{validationErrors.primaryBestContactPhone}</div>}
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Alternate Phone No.</label>
                                    <div style={{ display: 'flex' }}>
                                        <input
                                            type="text"
                                            name="primaryAlternatePhone"
                                            value={formData.primaryAlternatePhone}
                                            onChange={handleChange}
                                            style={{ ...inputStyle, borderTopRightRadius: 0, borderBottomRightRadius: 0, width: '70%' }}
                                        />
                                        <select
                                            name="primaryAlternatePhoneType"
                                            value={formData.primaryAlternatePhoneType}
                                            onChange={handleChange}
                                            style={{ ...inputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, width: '30%' }}
                                        >
                                            <option value="cell">Cell</option>
                                            <option value="home">Home</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                <div style={{ ...fieldGroupStyle, gridColumn: '1 / 3' }}>
                                    <label style={labelStyle}>Place of Employment</label>
                                    <input
                                        type="text"
                                        name="primaryEmployer"
                                        value={formData.primaryEmployer}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Employer Phone No.</label>
                                    <input
                                        type="text"
                                        name="primaryEmployerPhone"
                                        value={formData.primaryEmployerPhone}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Position</label>
                                    <input
                                        type="text"
                                        name="primaryPosition"
                                        value={formData.primaryPosition}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={labelStyle}>Shift</label>
                                <input
                                    type="text"
                                    name="primaryShift"
                                    value={formData.primaryShift}
                                    onChange={handleChange}
                                    style={{ ...inputStyle, maxWidth: '200px' }}
                                />
                            </div>
                        </div>
                        
                        {/* Co-Buyer Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Co-Buyer Verification</h3>
                            
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="hasCoBuyer"
                                        checked={formData.hasCoBuyer}
                                        onChange={handleChange}
                                        style={{ marginRight: '8px' }}
                                    />
                                    Include Co-Buyer Information
                                </label>
                            </div>
                            
                            {formData.hasCoBuyer && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '15px' }}>
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>Street Address</label>
                                            <input
                                                type="text"
                                                name="coBuyerStreetAddress"
                                                value={formData.coBuyerStreetAddress}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                            {validationErrors.coBuyerStreetAddress && <div style={errorStyle}>{validationErrors.coBuyerStreetAddress}</div>}
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>City</label>
                                            <input
                                                type="text"
                                                name="coBuyerCity"
                                                value={formData.coBuyerCity}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                            {validationErrors.coBuyerCity && <div style={errorStyle}>{validationErrors.coBuyerCity}</div>}
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>State</label>
                                            <input
                                                type="text"
                                                name="coBuyerState"
                                                value={formData.coBuyerState}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                            {validationErrors.coBuyerState && <div style={errorStyle}>{validationErrors.coBuyerState}</div>}
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>ZIP</label>
                                            <input
                                                type="text"
                                                name="coBuyerZip"
                                                value={formData.coBuyerZip}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                            {validationErrors.coBuyerZip && <div style={errorStyle}>{validationErrors.coBuyerZip}</div>}
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>Best Contact Phone No.</label>
                                            <div style={{ display: 'flex' }}>
                                                <input
                                                    type="text"
                                                    name="coBuyerBestContactPhone"
                                                    value={formData.coBuyerBestContactPhone}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, borderTopRightRadius: 0, borderBottomRightRadius: 0, width: '70%' }}
                                                />
                                                <select
                                                    name="coBuyerBestContactPhoneType"
                                                    value={formData.coBuyerBestContactPhoneType}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, width: '30%' }}
                                                >
                                                    <option value="cell">Cell</option>
                                                    <option value="home">Home</option>
                                                </select>
                                            </div>
                                            {validationErrors.coBuyerBestContactPhone && <div style={errorStyle}>{validationErrors.coBuyerBestContactPhone}</div>}
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>Alternate Phone No.</label>
                                            <div style={{ display: 'flex' }}>
                                                <input
                                                    type="text"
                                                    name="coBuyerAlternatePhone"
                                                    value={formData.coBuyerAlternatePhone}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, borderTopRightRadius: 0, borderBottomRightRadius: 0, width: '70%' }}
                                                />
                                                <select
                                                    name="coBuyerAlternatePhoneType"
                                                    value={formData.coBuyerAlternatePhoneType}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: 0, width: '30%' }}
                                                >
                                                    <option value="cell">Cell</option>
                                                    <option value="home">Home</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                        <div style={{ ...fieldGroupStyle, gridColumn: '1 / 3' }}>
                                            <label style={labelStyle}>Place of Employment</label>
                                            <input
                                                type="text"
                                                name="coBuyerEmployer"
                                                value={formData.coBuyerEmployer}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>Employer Phone No.</label>
                                            <input
                                                type="text"
                                                name="coBuyerEmployerPhone"
                                                value={formData.coBuyerEmployerPhone}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>
                                        
                                        <div style={fieldGroupStyle}>
                                            <label style={labelStyle}>Position</label>
                                            <input
                                                type="text"
                                                name="coBuyerPosition"
                                                value={formData.coBuyerPosition}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginTop: '15px' }}>
                                        <label style={labelStyle}>Shift</label>
                                        <input
                                            type="text"
                                            name="coBuyerShift"
                                            value={formData.coBuyerShift}
                                            onChange={handleChange}
                                            style={{ ...inputStyle, maxWidth: '200px' }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {/* Miscellaneous Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Miscellaneous Verification</h3>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Authorized Third Party (ATP)</label>
                                    <input
                                        type="text"
                                        name="authorizedThirdParty"
                                        value={formData.authorizedThirdParty}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                                
                                <div style={fieldGroupStyle}>
                                    <label style={labelStyle}>Relationship</label>
                                    <input
                                        type="text"
                                        name="thirdPartyRelationship"
                                        value={formData.thirdPartyRelationship}
                                        onChange={handleChange}
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={labelStyle}>Do you have an email address that you would like to have on file for electronic correspondence?</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="hasEmailForCorrespondence"
                                            value="yes"
                                            checked={formData.hasEmailForCorrespondence === 'yes'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Yes
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="hasEmailForCorrespondence"
                                            value="no"
                                            checked={formData.hasEmailForCorrespondence === 'no'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        No
                                    </label>
                                    
                                    {formData.hasEmailForCorrespondence === 'yes' && (
                                        <div style={{ flex: 1 }}>
                                            <input
                                                type="email"
                                                name="emailForCorrespondence"
                                                value={formData.emailForCorrespondence}
                                                onChange={handleChange}
                                                placeholder="Email address to update"
                                                style={inputStyle}
                                            />
                                            {validationErrors.emailForCorrespondence && <div style={errorStyle}>{validationErrors.emailForCorrespondence}</div>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Consent to Contact Verification */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Consent to Contact Verification</h3>
                            
                            <div style={fieldGroupStyle}>
                                <label style={labelStyle}>
                                    Do you agree that American Finance may contact you by phone call, text message, e-mail or voicemail on the phone 
                                    numbers you have provided using a prerecorded or artificial voice message or automatic telephone dialing system
                                    for any purpose related to the servicing of your account?
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consentToContact"
                                            value="yes"
                                            checked={formData.consentToContact === 'yes'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Yes
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="consentToContact"
                                            value="no"
                                            checked={formData.consentToContact === 'no'}
                                            onChange={handleChange}
                                            style={{ marginRight: '5px' }}
                                        />
                                        No
                                    </label>
                                    
                                    {formData.consentToContact === 'yes' && (
                                        <div style={{ flex: 1 }}>
                                            <input
                                                type="text"
                                                name="verifiedCellPhone"
                                                value={formData.verifiedCellPhone}
                                                onChange={handleChange}
                                                placeholder="Verify the cell phone number for phone calls and texting"
                                                style={inputStyle}
                                            />
                                            {validationErrors.verifiedCellPhone && <div style={errorStyle}>{validationErrors.verifiedCellPhone}</div>}
                                        </div>
                                    )}
                                </div>
                                {validationErrors.consentToContact && <div style={errorStyle}>{validationErrors.consentToContact}</div>}
                            </div>
                        </div>
                        
                        {/* References */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>References</h3>
                            <p style={{ marginBottom: '15px', fontSize: '14px' }}>
                                Confirm listed references or update as necessary <span style={{ color: '#ffeb3b', fontWeight: 'bold' }}>(Three references are needed)</span>
                            </p>
                            
                            {validationErrors.references && <div style={{ ...errorStyle, marginBottom: '10px' }}>{validationErrors.references}</div>}
                            
                            {warnings.references && (
                                <div className="warning-message" style={{ 
                                    backgroundColor: '#fff3cd', 
                                    color: '#856404', 
                                    padding: '10px', 
                                    borderRadius: '4px',
                                    marginBottom: '15px' 
                                }}>
                                    <i className="fas fa-exclamation-triangle"></i> {warnings.references}
                                </div>
                            )}
                            
                            {formData.references.map((reference, index) => (
                                <div key={index} style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '1fr 1fr 1fr', 
                                    gap: '15px',
                                    marginBottom: '15px',
                                    padding: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '4px'
                                }}>
                                    <div style={fieldGroupStyle}>
                                        <label style={labelStyle}>Name #{index + 1}</label>
                                        <input
                                            type="text"
                                            value={reference.name}
                                            onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                                            style={inputStyle}
                                        />
                                        {validationErrors[`references[${index}].name`] && <div style={errorStyle}>{validationErrors[`references[${index}].name`]}</div>}
                                    </div>
                                    
                                    <div style={fieldGroupStyle}>
                                        <label style={labelStyle}>Relationship</label>
                                        <input
                                            type="text"
                                            value={reference.relationship}
                                            onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                                            style={inputStyle}
                                        />
                                        {validationErrors[`references[${index}].relationship`] && <div style={errorStyle}>{validationErrors[`references[${index}].relationship`]}</div>}
                                    </div>
                                    
                                    <div style={fieldGroupStyle}>
                                        <label style={labelStyle}>Phone No.</label>
                                        <input
                                            type="text"
                                            value={reference.phoneNo}
                                            onChange={(e) => handleReferenceChange(index, 'phoneNo', e.target.value)}
                                            style={inputStyle}
                                        />
                                        {validationErrors[`references[${index}].phoneNo`] && <div style={errorStyle}>{validationErrors[`references[${index}].phoneNo`]}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Additional Comments */}
                        <div style={sectionStyle}>
                            <h3 style={sectionTitleStyle}>Additional Comments</h3>
                            <textarea
                                name="additionalComments"
                                value={formData.additionalComments}
                                onChange={handleChange}
                                style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                                placeholder="Enter any additional notes or comments here..."
                            />
                        </div>
                        
                        {/* Form submission */}
                        {submitError && (
                            <div style={{ 
                                padding: '10px 15px',
                                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                borderRadius: '4px',
                                marginBottom: '20px',
                                color: '#f44336',
                                border: '1px solid #f44336'
                            }}>
                                {submitError}
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '25px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#555',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                Cancel
                            </button>
                            
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{
                                    padding: '10px 30px',
                                    backgroundColor: isSubmitting ? '#0066b2' : '#0099ff',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    fontWeight: '500'
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Save'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CustomerVerificationForm;